import { datadogLogs } from '@datadog/browser-logs';
import { type FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { conditionalString } from '@strata/helper-functions';
import { ErrorToast } from '@strata/react-toast-message';
import { type ErrorInfo } from 'react';

interface FMHError {
    data?: { detail?: string };
}

interface ParsedError {
    message?: string;
    details?: string;
}

export const handleError = (
    message: string,
    error?: FetchBaseQueryError | Error | { error: Error; info: ErrorInfo },
) => {
    const isAbortError = (
        error as {
            status: 'FETCH_ERROR';
            data?: undefined;
            error: string;
        }
    )?.error?.startsWith('AbortError');

    // Parse and format the error string
    let parsedError: ParsedError | undefined;
    try {
        const errorDetail = (
            JSON.parse((error as Error)?.message) as { detail?: ParsedError | string } | undefined
        )?.detail;
        parsedError = typeof errorDetail === 'object' ? errorDetail : { message: errorDetail };
    } catch {
        parsedError = { message: (error as Error)?.message };
    } finally {
        const errorMessage = parsedError?.message ?? message;

        let errorMessageDetails = (error as FMHError)?.data?.detail ?? parsedError?.details;
        errorMessageDetails = conditionalString(!!errorMessageDetails, ` ${errorMessageDetails}`);
        const displayMessage = `${errorMessage}${errorMessageDetails}`;

        // Handle AbortError separately in case of fetch cancellation
        // Introduced in @reduxjs/toolkit v2.9.2
        // On component unmount while a fetch is ongoing, an AbortError is thrown
        // We don't want to show a toast for these errors as everything is working as expected
        if (!isAbortError) {
            ErrorToast({ message: displayMessage });
        }

        datadogLogs.logger.error(displayMessage, { error });
        // eslint-disable-next-line no-console
        console.error(displayMessage, error);
    }
};

export const handleRTKQueryError = (message: string) => (error: FetchBaseQueryError) => {
    handleError(message, error);
    return error;
};
